import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const USERNAME_REGEX = /^[A-Za-z0-9]+$/;

const AdminSignup = () => {
  const navigate = useNavigate();
  const latestUsernameRef = useRef("");
  const [form, setForm] = useState({
    name: "",
    username: "",
    dob: "",
    email: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [usernameStatus, setUsernameStatus] = useState("");
  const [error, setError] = useState("");

  const validateUsername = (value) => {
    if (value.length < 5 || value.length > 25) {
      return "Username must be 5-25 characters.";
    }
    if (!USERNAME_REGEX.test(value)) {
      return "Use letters and numbers only.";
    }
    return "";
  };

  const checkUsernameAvailability = async (value) => {
    const trimmed = value.trim();
    latestUsernameRef.current = trimmed;
    if (!trimmed) {
      setIsUsernameAvailable(null);
      setUsernameStatus("");
      setUsernameChecking(false);
      return false;
    }
    const validationMessage = validateUsername(trimmed);
    if (validationMessage) {
      setIsUsernameAvailable(false);
      setUsernameStatus(validationMessage);
      setUsernameChecking(false);
      return false;
    }

    setUsernameStatus("");
    setIsUsernameAvailable(null);
    setUsernameChecking(true);
    try {
      const usersRef = collection(db, "users");
      const usernameQuery = query(
        usersRef,
        where("username", "==", trimmed)
      );
      const snapshot = await getDocs(usernameQuery);
      if (latestUsernameRef.current !== trimmed) return false;
      const available = snapshot.empty;
      setIsUsernameAvailable(available);
      setUsernameStatus(
        available ? "Username is available" : "Username is not available"
      );
      return available;
    } catch (err) {
      console.error("Error checking username availability:", err);
      if (latestUsernameRef.current !== trimmed) return false;
      setIsUsernameAvailable(false);
      setUsernameStatus("Could not check username availability.");
      return false;
    } finally {
      if (latestUsernameRef.current === trimmed) {
        setUsernameChecking(false);
      }
    }
  };

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUsernameChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      username: value,
    }));
    checkUsernameAvailability(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const name = form.name.trim();
    const username = form.username.trim();
    const email = form.email.trim();
    const dob = form.dob;
    const password = form.password;

    if (!name || !username || !email || !dob || !password) {
      setError("Please fill in all fields.");
      return;
    }
    const usernameValidation = validateUsername(username);
    if (usernameValidation) {
      setError(usernameValidation);
      return;
    }

    setSubmitting(true);
    try {
      const available = await checkUsernameAvailability(username);
      if (!available) {
        setError("Please choose an available username.");
        setSubmitting(false);
        return;
      }
      const credential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const userId = credential.user.uid;
      await setDoc(doc(db, "users", userId), {
        name,
        username,
        email,
        dob,
        adminEnabled: false,
        createdAt: serverTimestamp(),
      });
      await signOut(auth);
      navigate("/login", {
        replace: true,
        state: {
          message:
            "Account created. Ask a developer or super admin to assign your admin role.",
        },
      });
    } catch (err) {
      console.error("Admin signup failed", err);
      setError(err?.message || "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-header">
          <div className="brand-mark">AA</div>
          <div>
            <h1>Create admin account</h1>
            <p>Sign up for a basic admin profile. Roles are assigned later.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <label className="field">
            <span>Full name</span>
            <input
              type="text"
              value={form.name}
              onChange={updateField("name")}
              placeholder="Alex Morgan"
              required
            />
          </label>

          <label className="field">
            <span>Username</span>
            <input
              type="text"
              value={form.username}
              onChange={handleUsernameChange}
              placeholder="alexm"
              required
            />
            {usernameChecking ? (
              <div className="helper-text">Checking availability...</div>
            ) : null}
            {usernameStatus ? (
              <div
                className={`availability ${
                  isUsernameAvailable ? "success" : "error"
                }`}
              >
                {usernameStatus}
              </div>
            ) : null}
          </label>

          <label className="field">
            <span>Date of birth</span>
            <input
              type="date"
              value={form.dob}
              onChange={updateField("dob")}
              required
            />
          </label>

          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={updateField("email")}
              placeholder="admin@aaplay.com"
              required
            />
          </label>

          <label className="field">
            <span>Password</span>
            <input
              type="password"
              value={form.password}
              onChange={updateField("password")}
              placeholder="********"
              required
            />
          </label>

          {error && <div className="alert error">{error}</div>}

          <button className="button primary" type="submit" disabled={submitting}>
            {submitting ? "Creating account..." : "Create account"}
          </button>
        </form>

        <div className="helper-text">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>

      <div className="auth-side">
        <div className="auth-side-inner">
          <h2>Request admin access</h2>
          <p>
            Your account is created without a role. A developer or super admin
            must assign access before you can enter the console.
          </p>
          <div className="auth-note">
            <div className="note-title">Next steps</div>
            <ul>
              <li>Submit the form with your basic details.</li>
              <li>Wait for an admin to grant a role.</li>
              <li>Sign in after approval.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignup;
